import { FormFooterBar } from '@features/cms/components/ui/FormFooterBar'
import {
  Box,
  Chip,
  TextField,
  Typography
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/system'
import { MarkdownEditor } from '@features/cms/components/MarkdownEditor'
import { useUnsavedChangesAlert } from '@hooks/useUnsavedChangesAlert'

import { updateContent } from '../mutation-helper/content'
import { syncContentTexts } from '../mutation-helper/text'

import { VoiceoverMarkerSelection } from './components/VoiceoverMarkerSelection'

import type { ContentEditorComponentProps } from '.'

type UpdateInfoPopupContentFormInput = {
  texts: {
    title?: string
    text: string
  },
  config: Record<string, any>
}

export const InfoPopupContentEditor: React.FC<ContentEditorComponentProps> = ({ nuggetId, content, onEdited }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [uploadError, setUploadError] = useState<any>(null)

  const defaultValues = useMemo<UpdateInfoPopupContentFormInput>(() => {
    return {
      texts: content.texts,
      config: content.config ?? {}
    }
  }, [content])

  const methods = useForm<UpdateInfoPopupContentFormInput>({
    defaultValues
  })

  useUnsavedChangesAlert(methods.formState.isDirty)

  useEffect(() => {
    onEdited?.(methods.formState.isDirty)
  }, [methods.formState.isDirty])

  const canSave = useMemo(
    () => methods.formState.isValid && methods.formState.isDirty,
    [methods.formState.isDirty, methods.formState.isValid]
  )

  useEffect(() => {
    methods.reset(defaultValues)
  }, [defaultValues])

  const onSubmit = async (submittedData: UpdateInfoPopupContentFormInput) => {
    setLoading(true)

    const texts = await syncContentTexts(content, submittedData.texts)

    try {
      await updateContent(content.id, {
        nuggetId,
        texts,
        config: submittedData.config,
        order: content.order
      })
    } catch (e) {
      setUploadError(e)
    }

    setLoading(false)
    setShowSnackbar(true)
  }

  const closeSnackbar = () => {
    setShowSnackbar(false)
  }

  return (
    <FormProvider {...methods}>
      <form
        style={{ display: 'flex', flex: 1, flexDirection: 'column', overflowY: 'hidden' }}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <Stack spacing={2} p={4} flex={1} sx={{ overflowY: 'auto' }}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
            <Box flex={1}>
              <Chip label={t('common.title')} size="small" sx={{ marginBottom: '8px' }} />
              <Box>
                <TextField {...methods.register('texts.title')}
                  sx={{ width: '100%' }}
                  label={t('common.title')}
                  name="texts.title"
                  placeholder={t('edit.content.infoPopupTitlePlaceholder')}
                />
              </Box>
              <Typography fontSize="small" sx={{ opacity: 0.7 }}>Standard: {t('edit.content.infoPopupTitlePlaceholder')}</Typography>
            </Box>
            <Box width="300px" mt={1.25}>
              <VoiceoverMarkerSelection name="config.voiceoverMarker.text" />
            </Box>
          </Box>

          <Box>
            <Chip label={t('common.text')} size="small" sx={{ marginBottom: '8px' }} />
            <MarkdownEditor name='texts.text' />
          </Box>
        </Stack>

        <FormFooterBar
          disabled={!canSave}
          loading={loading}
          uploadError={uploadError}
          showSnackbar={showSnackbar}
          closeSnackbar={closeSnackbar}
        />
      </form>
    </FormProvider>)
}
